/**
 * Agent finding preview — same overlay as Document Review after the AI button:
 * full live paragraph (both parties' Word redlines) with the agent's suggested
 * wording applied as an AI track-change on top.
 */

import { previewHybridOps } from '../hybridApply/previewHybrid';
import {
  foldHybridCleanTarget,
  resolveSuggestedEditPreviewHtml,
} from '../hybridApply/suggestedEdit';
import { RobustXmlTrackChangesGenerator } from '../rephrasing/RobustXmlTrackChangesGenerator';
import { ProofreadWordAdapter } from '../proofread/ProofreadWordAdapter';
import { spliceSnippetIntoParagraph } from '../../utils/locateSnippetInParagraph';
import { resolveTrackChangeAuthorSide } from '../../utils/reviewPartyContext';
import {
  formatOoxmlParagraphForModel,
  markupToAcceptedPlainText,
} from '../../utils/trackChangesModelText';
import {
  ooxmlToPartyTrackChangesHtml,
  prepareTrackChangesPreviewHtml,
} from '../../utils/trackChangePreviewHtml';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { logError } from '../../utils/logging';

function composeFromOoxml(
  ooxml: string | null | undefined,
  originalSnippet: string,
  suggestedSnippet: string,
): string | null {
  if (!ooxml) return null;
  const marked = formatOoxmlParagraphForModel(ooxml, resolveTrackChangeAuthorSide);
  const accepted = markupToAcceptedPlainText(marked) || marked;
  if (!accepted.trim()) return null;
  return spliceSnippetIntoParagraph(accepted, originalSnippet, suggestedSnippet);
}

export async function buildAgentIssuePreviewHtml(args: {
  paragraphIndex: number;
  originalText: string;
  suggestedText: string;
  ooxml?: string | null;
}): Promise<string | null> {
  const composed =
    (await ProofreadWordAdapter.composeParagraphTargetFromSnippet(
      args.paragraphIndex,
      args.originalText,
      args.suggestedText,
    )) || composeFromOoxml(args.ooxml, args.originalText, args.suggestedText);

  const target = composed ? foldHybridCleanTarget(composed) : '';

  let hybridHtml: string | null = null;
  if (target && args.paragraphIndex >= 0) {
    try {
      const hybrid = await previewHybridOps(args.paragraphIndex, target);
      hybridHtml = hybrid?.html ?? null;
    } catch (err) {
      logError('[AgentIssuePreview] hybrid preview failed', err);
    }
  }

  let fallbackHtml: string | null = null;
  if (args.ooxml && target) {
    try {
      const edited = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
        args.ooxml,
        target,
        'AI Suggestion',
      );
      fallbackHtml = edited ? TrackChangesParser.convertOoxmlToHtml(edited) : null;
    } catch (err) {
      logError('[AgentIssuePreview] OOXML suggestion preview failed', err);
    }
  }
  if (!fallbackHtml && args.ooxml) {
    fallbackHtml = ooxmlToPartyTrackChangesHtml(args.ooxml);
  }
  if (!fallbackHtml) {
    fallbackHtml = ProofreadWordAdapter.buildTrackChangesHtml(
      args.originalText,
      args.suggestedText,
    );
  }

  const raw = resolveSuggestedEditPreviewHtml({
    hybridEnabled: true,
    hybridPreviewHtml: hybridHtml,
    fallbackHtml,
  });
  return raw ? prepareTrackChangesPreviewHtml(raw) : null;
}

export function liveParagraphPreviewHtml(ooxml: string | null | undefined): string | null {
  return ooxml ? ooxmlToPartyTrackChangesHtml(ooxml) : null;
}
